import { useState } from 'react';
import {
  Card,
  CardHeader,
  Badge,
  Button,
  Input,
  Select,
  Table,
  LoadingSkeleton,
  type Column,
} from '@nexora/design-system';
import {
  useRegimeSimulator,
  type RegimeProjection,
} from './useRegimeSimulator';
import { formatCurrency, formatDate, EMPTY } from '../../../shared/utils/format';

/* ───────────────────────────────────────────────────────────────────────────
   Portado de base-html, slug `erpone_simulador_de_regimes_tribut_rios`
   → Simulador de Regimes Tributários — POST /fiscal/regime-simulator.

   Da referência vieram: o formulário de projeção (receita anual, UF origem/
   destino, ano), o comparativo anual por regime (Simples/Lucro Presumido/
   Lucro Real) com alíquota efetiva, o destaque "RECOMENDADO" e o breakdown
   por tributo (IRPJ, CSLL, PIS/COFINS, INSS Patronal...).

   Não vieram (não existem no contrato): campos de despesas operacionais,
   folha de pagamento e lucro líquido — o simulador roda APENAS com receita
   anual + mix de itens + UFs; o motor tributário real calcula o resto.

   O `das` (projeção oficial do Simples pela tabela LC 123/2006) é exibido
   quando o regime Simples retorna a projeção.
   ─────────────────────────────────────────────────────────────────────────── */

const OPERATION_OPTIONS = [
  { value: 'VENDA', label: 'Venda' },
  { value: 'COMPRA', label: 'Compra' },
  { value: 'DEVOLUCAO_VENDA', label: 'Devolução de venda' },
  { value: 'DEVOLUCAO_COMPRA', label: 'Devolução de compra' },
  { value: 'TRANSFERENCIA', label: 'Transferência' },
  { value: 'SERVICO', label: 'Serviço' },
  { value: 'OUTROS', label: 'Outros' },
];

const ANEXO_OPTIONS = [
  { value: 'ANEXO_I', label: 'Anexo I — Comércio' },
  { value: 'ANEXO_II', label: 'Anexo II — Indústria' },
  { value: 'ANEXO_III', label: 'Anexo III — Serviços / locação' },
  { value: 'ANEXO_IV', label: 'Anexo IV — Serviços com CPP' },
  { value: 'ANEXO_V', label: 'Anexo V — Serviços sem CPP' },
];

const UFS = [
  'AC', 'AL', 'AM', 'AP', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MG', 'MS',
  'MT', 'PA', 'PB', 'PE', 'PI', 'PR', 'RJ', 'RN', 'RO', 'RR', 'RS', 'SC',
  'SE', 'SP', 'TO',
];

function formatPct(v: number | null | undefined): string {
  if (v === null || v === undefined || Number.isNaN(v)) return EMPTY;
  return `${Number(v).toFixed(1).replace('.', ',')}%`;
}

const RECOMENDADO = 'RECOMENDADO';

export function RegimeSimulatorPage(): JSX.Element {
  const [annualRevenue, setAnnualRevenue] = useState('1200000');
  const [originUf, setOriginUf] = useState('SP');
  const [destinationUf, setDestinationUf] = useState('SP');
  const [operationType, setOperationType] = useState('VENDA');
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [simplesAnexo, setSimplesAnexo] = useState('ANEXO_I');

  const mutation = useRegimeSimulator();

  function handleSimulate(): void {
    const revenue = Number(annualRevenue);
    if (!Number.isFinite(revenue) || revenue <= 0) return;
    mutation.mutate({
      annualRevenue: revenue,
      operationType,
      originUf,
      destinationUf,
      year: Number(year) || undefined,
      simplesAnexo,
    });
  }

  const result = mutation.data;

  const projectionColumns: Column<RegimeProjection>[] = [
    {
      header: 'Regime',
      accessor: 'label',
      render: (v: string, row: RegimeProjection) => (
        <span className="regime-name">
          <strong>{v}</strong>
          {result?.recommendation?.regime === row.regime && (
            <Badge size="sm" variant="success">{RECOMENDADO}</Badge>
          )}
        </span>
      ),
    },
    {
      header: 'Carga estimada (anual)',
      accessor: 'estimatedTotalTax',
      render: (v: number) => <span className="tabular-nums">{formatCurrency(v)}</span>,
      align: 'right',
    },
    {
      header: 'Alíquota efetiva',
      accessor: 'effectiveRatePct',
      render: (v: number) => <span className="tabular-nums">{formatPct(v)}</span>,
      align: 'right',
    },
    {
      header: 'Economia vs 2º colocado',
      accessor: 'regime',
      render: (_v: string, row: RegimeProjection) => {
        const rec = result?.recommendation;
        if (!rec || rec.regime === row.regime) return <span className="cell-muted">—</span>;
        return <span className="tabular-nums">{formatCurrency(rec.annualSavingsVsSecond)}</span>;
      },
      align: 'right',
    },
  ];

  return (
    <div className="regime-simulator-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">
            Simulador de Regimes Tributários
          </h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Projeção anual por regime usando o motor tributário real e as tabelas oficiais do Simples Nacional.
          </p>
        </div>
      </div>

      {/* Form */}
      <Card padding="1rem" variant="outlined" className="filter-card">
        <div className="regime-form">
          <Input
            label="Receita bruta anual projetada (R$)"
            type="number"
            min={0.01}
            step="0.01"
            value={annualRevenue}
            onChange={(e) => setAnnualRevenue(e.target.value)}
          />
          <Select label="UF de origem" value={originUf} onChange={(e) => setOriginUf(e.target.value)}>
            {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
          </Select>
          <Select label="UF de destino" value={destinationUf} onChange={(e) => setDestinationUf(e.target.value)}>
            {UFS.map((u) => <option key={u} value={u}>{u}</option>)}
          </Select>
          <Select label="Tipo de operação" value={operationType} onChange={(e) => setOperationType(e.target.value)}>
            {OPERATION_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
          <Input label="Ano" type="number" min={2026} max={2033} value={year} onChange={(e) => setYear(e.target.value)} />
          <Select label="Anexo do Simples (DAS)" value={simplesAnexo} onChange={(e) => setSimplesAnexo(e.target.value)}>
            {ANEXO_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </Select>
        </div>
        <div className="regime-form__actions">
          <Button
            variant="primary"
            onClick={handleSimulate}
            disabled={mutation.isPending || !(Number(annualRevenue) > 0)}
          >
            {mutation.isPending ? 'Calculando…' : 'Simular regimes'}
          </Button>
        </div>
      </Card>

      {/* Results */}
      {mutation.isPending && (
        <div className="regime-loading">
          <LoadingSkeleton count={3} height="120px" borderRadius="12px" />
        </div>
      )}

      {mutation.isError && !mutation.isPending && (
        <Card className="regime-error">
          <p className="m-0" style={{ fontWeight: 600, color: '#ba1a1a' }}>Erro ao simular regimes</p>
          <p className="m-0" style={{ fontSize: '0.875rem', color: '#74777f' }}>
            {mutation.error instanceof Error ? mutation.error.message : 'Erro desconhecido'}
          </p>
        </Card>
      )}

      {result && !mutation.isPending && (
        <>
          {/* Recommendation banner */}
          {result.recommendation && (
            <Card className="regime-recommendation" variant="elevated">
              <div className="regime-recommendation__body">
                <Badge size="sm" variant="success">{RECOMENDADO}</Badge>
                <div>
                  <strong className="font-headline-sm">{result.recommendation.label}</strong>
                  <p className="m-0 text-on-surface-variant">
                    Carga estimada {formatCurrency(result.recommendation.estimatedTotalTax)}/ano
                    {result.recommendation.annualSavingsVsSecond > 0 &&
                      ` — economia de ${formatCurrency(result.recommendation.annualSavingsVsSecond)} vs o segundo colocado`}
                  </p>
                </div>
              </div>
            </Card>
          )}

          {/* Comparison table */}
          <Card variant="outlined" padding="0">
            <CardHeader className="px-md pt-md" action={
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                Receita: {formatCurrency(result.annualRevenue)} • {formatDate(result.period.startDate)} — {formatDate(result.period.endDate)}
              </span>
            }>
              <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Projeção financeira anual por regime</h3>
            </CardHeader>
            <Table
              columns={projectionColumns}
              data={result.regimes}
              keyExtractor={(r) => r.regime}
            />
          </Card>

          {/* DAS + notes */}
          {result.regimes.map((r) => (
            <Card key={r.regime} variant="outlined" padding="0" className="regime-detail">
              <CardHeader className="px-md pt-md">
                <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">{r.label}</h3>
              </CardHeader>
              <div className="regime-detail__body">
                {r.das && (
                  <div className="regime-das">
                    <div className="regime-das__row"><span>DAS estimado (tabela oficial)</span><strong>{formatCurrency(r.das.dasAnual)}/ano</strong></div>
                    <div className="regime-das__row"><span>Anexo</span><strong>{r.das.anexo}</strong></div>
                    <div className="regime-das__row"><span>Faixa</span><strong>{r.das.faixa}</strong></div>
                    <div className="regime-das__row"><span>Alíquota nominal</span><strong>{formatPct(r.das.aliquotaNominal)}</strong></div>
                    <div className="regime-das__row"><span>Alíquota efetiva</span><strong>{formatPct(r.das.aliquotaEfetiva)}</strong></div>
                  </div>
                )}
                {r.breakdown.length > 0 && (
                  <div className="regime-breakdown">
                    <strong className="regime-breakdown__title">Composição da carga (anual)</strong>
                    <table className="fiscal-table">
                      <thead>
                        <tr>
                          <th>Tributo</th>
                          <th align="right">Base (R$)</th>
                          <th align="right">Valor (R$)</th>
                          <th align="right">Alíquota</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.breakdown.map((b) => (
                          <tr key={b.taxType}>
                            <td><strong>{b.taxType}</strong></td>
                            <td align="right" className="tabular-nums">{formatCurrency(b.base)}</td>
                            <td align="right" className="tabular-nums">{formatCurrency(b.value)}</td>
                            <td align="right" className="tabular-nums">{formatPct(b.aliquota)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {r.notes.length > 0 && (
                  <ul className="regime-notes">
                    {r.notes.map((n, i) => <li key={i}>{n}</li>)}
                  </ul>
                )}
              </div>
            </Card>
          ))}

          {result.engineAudit.errors.length > 0 && (
            <Card className="regime-error">
              <p className="m-0" style={{ fontWeight: 600, color: '#ba1a1a' }}>Erros do motor durante a simulação</p>
              {result.engineAudit.errors.map((e, i) => (
                <p key={i} className="m-0" style={{ fontSize: '0.875rem' }}>{e}</p>
              ))}
            </Card>
          )}
        </>
      )}

      <style>{`
        .regime-simulator-page { padding: 1.5rem; }
        .regime-form { display: grid; grid-template-columns: repeat(auto-fit, minmax(180px, 1fr)); gap: 0.75rem; align-items: end; }
        .regime-form__actions { margin-top: 1rem; display: flex; justify-content: flex-end; }
        .regime-loading { margin-top: 1rem; display: grid; gap: 1rem; }
        .regime-error { margin-top: 1rem; padding: 1.5rem; display: grid; gap: 0.5rem; color: #74777f; }
        .regime-recommendation { margin: 1rem 0; padding: 1rem 1.25rem; }
        .regime-recommendation__body { display: flex; align-items: center; gap: 1rem; }
        .regime-name { display: flex; align-items: center; gap: 0.5rem; }
        .regime-detail { margin-top: 1rem; }
        .regime-detail__body { padding: 1rem; display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 1rem; }
        .regime-das { border: 1px solid #eceef0; border-radius: 10px; padding: 0.75rem 1rem; align-self: start; }
        .regime-das__row { display: flex; justify-content: space-between; gap: 0.75rem; padding: 0.375rem 0; border-bottom: 1px solid #f2f4f6; font-size: 0.8125rem; }
        .regime-das__row:last-child { border-bottom: none; }
        .regime-breakdown__title { display: block; margin-bottom: 0.5rem; }
        .regime-notes { margin: 0; padding: 0 0 0 1.25rem; color: #74777f; font-size: 0.8125rem; display: flex; flex-direction: column; gap: 0.25rem; }
        .fiscal-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .fiscal-table th {
          text-align: left; padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e;
          font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em;
          border-bottom: 2px solid #e0e3e5; white-space: nowrap; position: sticky; top: 0; z-index: 1;
        }
        .fiscal-table td { padding: 0.625rem 1rem; border-bottom: 1px solid #eceef0; vertical-align: middle; }
        .fiscal-table tbody tr:hover { background: #f8fafc; }
        .tabular-nums { font-variant-numeric: tabular-nums; }
        .cell-muted { color: #74777f; }
      `}</style>
    </div>
  );
}